<template>
    <div v-if="loaded">
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-input
                        disabled
                        label="Código"
                        v-model="model.codigo"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        :validations="validations.nome"
                        label="Nome"
                        uppercase
                        v-model="model.nome"
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-form-checkbox
                        :disabled="model.ativo && additionals.hasAtendimentosEmAndamento"
                        :label="false"
                        v-model="model.ativo"
                    >
                        Ativo
                    </rkm-form-checkbox>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :disabled="disableProcedimentoPrincipal"
                        :options="form.procedimentosPrincipais"
                        :validations="validations.procedimentoPrincipal"
                        @change="handleProcedimentoPrincipalChange"
                        label="Procedimento principal"
                        show-default-option
                        text-field="descricao"
                        v-model="model.procedimentoCodigo"
                        value-field="codigo"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        :validations="validations.linhaCuidado"
                        label="Linha de cuidado"
                        uppercase
                        v-model="model.linhaCuidado"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :maxlength="validations.descricaoGeral.max"
                        :validations="validations.descricaoGeral"
                        label="Descrição geral"
                        rows="4"
                        v-model="model.descricaoGeral"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :maxlength="validations.objetivoAssistencial.max"
                        :validations="validations.objetivoAssistencial"
                        label="Objetivo assistencial"
                        rows="4"
                        v-model="model.objetivoAssistencial"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="!model.ativo">
                <rkm-col>
                    <rkm-form-textarea
                        :maxlength="validations.motivoInativacao.max"
                        :validations="validations.motivoInativacao"
                        label="Motivo da inativacao"
                        rows="4"
                        v-model="model.motivoInativacao"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <Portaria :data="{ model }" />
        <EtapaClinica :data="{ form, model }" />

        <Procedimento
            :data="{ form, model }"
            ref="Procedimento"
        />

        <UnidadeExecutora :data="{ form, model }" />
    </div>
</template>

<script>
// Components.
import Portaria from './components/portaria/Index'
import EtapaClinica from './components/etapa-clinica/Index'
import Procedimento from './components/procedimento/Index'
import UnidadeExecutora from './components/unidade-executora/Index'

// Mixins.
import { error, repository, stringCase, object } from '@/mixins'

// Enums.
import etapaClinica from '@/enums/cadastro/oci/etapaClinica'

// Vuex.
import { mapActions } from 'vuex'

export default {
    components: {
        Portaria,
        EtapaClinica,
        Procedimento,
        UnidadeExecutora,
    },
    computed: {
        validations() {

            return {
                nome: { required: true, max: 80 },
                procedimentoPrincipal: { required: true },
                linhaCuidado: { max: 100 },
                descricaoGeral: { max: 1000 },
                objetivoAssistencial: { max: 1000 },
                motivoInativacao: { required: true, max: 500 },
            }
        },
        disableProcedimentoPrincipal() {

            return this.model.procedimentosPrimarios.length > 0
                || this.model.procedimentosSecundarios.length > 0
        },
    },
    created() {

        this.repositories = {
            oci: this.getConectaSusRepository('cadastro.oci'),
        }

        this.load()
    },
    data: () => ({
        loaded: false,
        additionals: null,
        form: null,
        model: null,
        enums: { etapaClinica },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            const request = this.id
                ? this.repositories.oci.edit(this.id)
                : this.repositories.oci.create()

            request
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleProcedimentoPrincipalChange(procedimentoPrincipalCodigo) {

            this.$refs.Procedimento.resetForms()

            this.loadProcedimentosCompativeis(procedimentoPrincipalCodigo)
        },
        loadProcedimentosCompativeis(procedimentoPrincipalCodigo) {

            if (!procedimentoPrincipalCodigo) {

                this.form.procedimentosPrimarios = []
                this.form.procedimentosSecundarios = []

                return
            }

            this.setLoading()

            this.repositories.oci.list({
                listar: 'procedimentos-compativeis',
                procedimento_codigo: procedimentoPrincipalCodigo
            })
                .then(({ data }) => {

                    this.form.procedimentosPrimarios = data.procedimentosPrimarios
                    this.form.procedimentosSecundarios = data.procedimentosSecundarios
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        save() {

            this.$refs.Form.validate().then(valid => {

                if (!valid) return

                if (!this.model.etapasClinicas.find(e => e.etapaClinicaId === this.enums.etapaClinica.ENTRADA_ALTA) && (
                    !this.model.etapasClinicas.find(e => e.etapaClinicaId === this.enums.etapaClinica.ENTRADA)
                    || !this.model.etapasClinicas.find(e => e.etapaClinicaId === this.enums.etapaClinica.RETORNO)
                    || !this.model.etapasClinicas.find(e => e.etapaClinicaId === this.enums.etapaClinica.ALTA)
                )) {

                    this.showValidationNotification('É obrigatório incluir ao menos as etapas clínicas de ENTRADA, RETORNO e ALTA, ou ENTRADA/ALTA.')

                    return
                }

                if (this.form.procedimentosPrimarios.length > 0 && this.model.procedimentosPrimarios.length === 0) {

                    this.showValidationNotification('É obrigatório incluir ao menos um procedimento obrigatório.')

                    return
                }

                if (this.model.unidadesExecutoras.length === 0) {

                    this.showValidationNotification('É obrigatório incluir ao menos uma unidade executora.')

                    return
                }

                this.setLoading()

                const request = this.id
                    ? this.repositories.oci.update(this.id, this.prepareData())
                    : this.repositories.oci.store(this.prepareData())

                request
                    .then(() => {

                        this.showSaveNotification()
                        this.$emit('save')
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            })
        },
        prepareData() {

            const data = this.clone(this.model)

            data.etapasClinicas = data.etapasClinicas.map(e => e.etapaClinicaId)

            return this.camelObjectKeysToSnakeRecursive(data)
        }
    },
    mixins: [error, repository, stringCase, object],
    props: {
        id: { type: Number, default: null },
    }
}
</script>